import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Add16 } from 'ui/icons/action/Add16';
import { Close16 } from 'ui/icons/action/Close16';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { IconButton, GhostSuccessButton, BUTTON_VARIANTS } from 'ui/button';
import { spacing } from 'ui/theme';
import { Link } from 'ui/links';

import { useTranslations } from '../../hooks/useTranslations';
import { SnippetFieldTestingIds, SnippetFieldsInputIds } from '../../types';

export const AddParameterButton = styled(GhostSuccessButton)`
  margin-top: ${spacing.half};
`;

const DeleteButton = styled(IconButton)`
  margin-left: -${spacing.half};
  margin-top: ${spacing.quarter};
`;

const LearnMoreLinkWrapper = styled.span`
  gap: ${spacing.quarter};
  display: flex;
`;

const ParameterRowWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 0 ${spacing.threeQuarter};
  align-items: start;
  margin-top: ${spacing.half};
`;

export const SnippetPositionalParameters = () => {
  const {
    clearErrors,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const fieldIdentifier = SnippetFieldsInputIds.positionalParameters;
  const { t } = useTranslations();
  const positionalParameters: string[] = watch(fieldIdentifier);

  const handlePositionalParametersChange = (newParameters: string[]) => {
    setValue(fieldIdentifier, newParameters, {
      shouldDirty: true,
    });
    clearErrors(fieldIdentifier);
  };

  const handleAddParameter = () => {
    const newParameters = [...positionalParameters];
    newParameters.push('');
    handlePositionalParametersChange(newParameters);
  };

  const handleChangeParameter = (parameterPosition: number, newParameter: string) => {
    const newParameters = [...positionalParameters];
    newParameters[parameterPosition] = newParameter;
    handlePositionalParametersChange(newParameters);
  };

  const handleDeleteParameter = (parameterPosition: number) => {
    const newParameters = [...positionalParameters];
    newParameters.splice(parameterPosition, 1);
    handlePositionalParametersChange(newParameters);
  };

  return (
    <FormField
      errorText={<>{errors[fieldIdentifier]?.message}</>}
      inputId={SnippetFieldTestingIds.PositionalParameters}
      labelText={t('label.snippetVariables')} // This field is shown to the user as "Variables"
      hasError={fieldIdentifier in errors}
      descriptionText={
        <LearnMoreLinkWrapper>
          {t('input.snippetVariables.descriptionText')}
          <Link
            variant="monochrome"
            size="caption"
            hasOpenNewTabIcon
            href={'https://support.iterable.com/hc/articles/4414796078868'}
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('link.learnMore')}
          </Link>
        </LearnMoreLinkWrapper>
      }
    >
      {positionalParameters.map((parameter, idx) => (
        <ParameterRowWrapper key={idx}>
          <InputText
            id="snippet-dynamic-text"
            onChange={(newParameter: string) => {
              handleChangeParameter(idx, newParameter);
            }}
            placeholder={t('input.snippetVariables.placeholder')}
            type="text"
            value={parameter}
            width={'93%'}
          />
          <DeleteButton onClick={() => handleDeleteParameter(idx)} variant={BUTTON_VARIANTS.GHOST}>
            <Close16 />
          </DeleteButton>
        </ParameterRowWrapper>
      ))}
      <AddParameterButton onClick={handleAddParameter}>
        <Add16 />
        {t('button.addVariable')}
      </AddParameterButton>
    </FormField>
  );
};
